import type { MediaManagerSDKError } from './../wix-ricos-editor/uploadStrategy/uploadTypes';
import type { ImageProps } from './../bundled-dependencies/wix-image';
import type {
  ImageComponentData,
  UpdateEntityFileSelectFunctionParams,
  UpdateEntityFileUploadFunctionParams,
} from './../ricos-content';
import type { EditorPluginConfig, ViewerPluginConfig } from './../ricos-types';

export const IMAGE_TYPE = 'wix-draft-plugin-image';

export type ImageEditorWixSettings = {
  initiator: string;
  siteToken: string;
  metaSiteId: string;
  mediaRoot: string;
};

/*
 * @deprecated Not possible to configure.
 */
export interface ImageConfig {
  size?: string;
  alignment?: string;
  showTitle?: boolean;
  showDescription?: boolean;
  link?: {
    anchor: unknown;
    url: string;
  };
  width?: number;
  textWrap?: string;
  spoiler?: SpoilerConfig;
}

type SpoilerConfig = {
  buttonContent?: string | undefined;
  description?: string | undefined;
  enabled: boolean | undefined;
};

type ImageSrc = {
  src: { id: string } | { url: string };
  width?: number;
  height?: number;
};

type UploadImage = (
  file: File
) => Promise<{ type: 'ok'; image: ImageSrc } | { type: 'error'; error: MediaManagerSDKError }>;

export interface ImagePluginEditorConfig extends EditorPluginConfig, ImageConfig {
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  /**
   * @default false
   * This is initial value of 'disableDownload' field in RicosDocument's ImageData for inserted images
   */
  disableDownload?: boolean;
  /**
   * @default false
   * This is initial value of 'disableExpand' field in RicosDocument's ImageData for inserted images
   */
  disableExpand?: boolean;

  /**
   * TODO: remove?
   * This function is called when the user clicks on the image editor button.
   * if not provided the Wix Media Manager modal will be invoked if possible,
   * otherwise the systems file picker will be opened
   */
  pickImage?: () => Promise<
    /**
     * This result type should be used when already ready media is provided
     * (e.g. from Wix Media Manager).
     */
    | {
        type: 'ready';
        images: ImageSrc[];
      }
    /**
     * When this option is provided the editor will go through image pending ->
     * ok/error flow while calling uploadFn to upload image.
     */
    | {
        type: 'files';
        files: File[];
        // if provided, the uploadImage will be overridden for these files
        upload?: UploadImage;
      }
  >;

  /**
   * This function will be called when image file needs to be added to content
   * (adding image via buttons, pasting, generating with AI etc.) If not
   * provided, the default implementation involving upload to Wix Media Manager
   * will be attempted.
   */
  uploadImage?: UploadImage;

  /** @deprecated @internal in favor of pickMedia */
  handleFileSelection?: (
    index: number | undefined,
    multiple: boolean,
    updateEntity: (args: UpdateEntityFileSelectFunctionParams<ImageComponentData[]>) => void,
    x: any,
    defaults: any
  ) => void;

  /** @deprecated @internal in favor of uploadImage */
  handleFileUpload?: (
    file: File,
    updateEntity: (args: UpdateEntityFileUploadFunctionParams<ImageComponentData>) => void
  ) => void;

  imageEditorWixSettings?: ImageEditorWixSettings;
  onImageEditorOpen?: () => void;

  /**
   * @deprecated Not possible to configure. Spoiler is disabled by default.
   */
  spoiler?: SpoilerConfig;
  /**
   * @deprecated Use disableDownload and disableExpand instead.
   */
  defaultData?: {
    config?: ImageConfig;
    disableExpand?: boolean;
  };
  /**
   * @deprecated
   */
  imageProps?: HTMLImageElement;
  /**
   * @deprecated
   */
  createGalleryForMultipleImages?: boolean;
  /**
   * @deprecated
   */
  imageEditor?: boolean;
  /**
   * @deprecated
   */
  consumer?: string;

  colors?: {
    color1?: string; // used for default textColor
    color2?: string;
    color3?: string;
    color4?: string;
    color5?: string;
    color6?: string;
    color7?: string;
    color8?: string;
    color9?: string;
    color10?: string;
  };
  onBorderColorAdded?: (color: string) => string[];
  getBorderColors?: () => string[];
}

export interface ImagePluginViewerConfig extends ViewerPluginConfig, ImageConfig {
  onExpand?: (blockkey: string) => unknown;
  /**
   * @default false
   * If set to true it will always remove the expand button from the image despite nodes data
   */
  disableExpand?: boolean;
  /**
   * @default false
   * @deprecated No usecases was found for it, currently doesn't work
   */
  disableDownload?: boolean;
  disableTransition?: boolean;
  imageProps?: ((src: Record<string, unknown>) => HTMLImageElement) | HTMLImageElement;
  encoding?: ImageProps['encoding'];

  colors?: {
    color1?: string; // used for default textColor
    color2?: string;
    color3?: string;
    color4?: string;
    color5?: string;
    color6?: string;
    color7?: string;
    color8?: string;
    color9?: string;
    color10?: string;
  };
}

export type ExtendedBlob = Blob & {
  lastModified: number;
  name: string;
  webkitRelativePath: string;
};
